import { NavigateFunction } from 'react-router-dom'
import { AnyAction } from 'redux'
import {
  all,
  call,
  fork,
  put,
  race,
  take,
  select,
  ActionPattern,
  takeLatest,
  delay
} from 'redux-saga/effects'
import { Channel } from 'redux-saga'
import { AxiosResponse } from 'axios'

import {
  setProgress,
  updateInstanceResources,
  setProgressBar,
  setUploaderParams,
  internalTestsStatus,
  setAppLockInfo,
  setRefreshAppNotifications,
  setScheduledTaskUpdate,
  setTranslationsProgress,
  setOpenAppWithUnconfirmedChanges,
  setPersistentOperation,
  clearPersistentOperation,
  startOperationStatusPolling,
  stopOperationStatusPolling,
  setOpenAppTaskResult
} from './actions'
import {
  MessageType,
  SocketMessage,
  SocketInstanceResources,
  SocketNodeFeedback,
  SocketInterfaceComponentsList,
  SocketInterfaceComponentResult,
  SocketInstanceStatusMessage,
  socketModelDebug,
  socketModelDebugBatch,
  SocketDownloadMessage,
  SocketUploadMessage,
  SocketMessageInternalTests,
  SocketAppInfo,
  SocketInterfaceFeedback,
  SocketNodeInterfaceFeedback,
  SocketInterfaceFilterComponentResult,
  SocketInterfaceFilterComponentsList,
  SocketAnalysisResponse,
  SocketScheduledTaskUpdated,
  SocketScheduledTaskFinished,
  SocketHtmlInterfaceBroadcast,
  SocketMessageTranslationsProgress,
  SocketNavigateToSectionMessage,
  NavigationSection,
  SocketOpenAppMessage,
  SocketNotification,
  SocketAgentMessage,
  SocketTraceUpdateMessage,
  RefreshSocketMessage,
  SocketAppNotificationMessage,
  SocketAsyncOperationCompleteMessage,
  SocketFileOperationProgressMessage,
  SocketOpenAppTaskResultMessage,
  SocketAgentPresenceMessage,
  SocketAgentActivityMessage,
  SocketAppInfoChangedMessage,
  SocketCaptureUserViewMessage,
  SocketInterfaceLayoutRefreshMessage
} from './constants'
import { createEventServer, ws } from './connection'
import {
  applySwitchedAppVersion,
  confirmNodeDefinitionsBeforeSaving
} from 'store/application/sagas'
import { SocketActionTypes } from './types'
import { ModelActionTypes } from 'store/model/types'
import { INSTANCE_ID, LOCALE } from 'common/api/storageConsts'
import { getRandomKey } from 'common/helpers/getRandomKey'
import { getTaskFinishedNotification } from 'pages/taskManager/helpers'
import { resolveAppLocale } from 'common/helpers/resolveAppLocale'
import { ScheduleTaskStates } from 'common/types/task'
import { enqueue } from 'common/notifier'
import { store } from 'index'
import { GridLayout } from 'pages/interfaces/containers/types'
import { fileManagerApi } from 'services/fileManager'
import { ApplicationState } from 'store'
import {
  checkHasActiveProcess,
  openApp,
  openAppOnNewInstance,
  setAnalysisResult,
  setAppHasUnsavedChanges,
  setAppVersionsDialog,
  setOpeningAppName,
  setAppInfo,
  closeAppFromOtherTab
} from 'store/application/actions'
import {
  setComponentIsLoading,
  setRefreshCurrentInterface,
  setUpdateDefaultInterfaceLayout,
  setApplicationComponent,
  addFilterToFilterComponentState,
  setFilterComponentsLoading,
  setFiltersApplied,
  setRefreshCurrentInterfaceLayout,
  notifyScenariosChanged,
  notifyInterfacesListChanged,
  receiveHtmlInterfaceBroadcast,
  removeActiveTab,
  removeApplicationComponents
} from 'store/interface/actions'
import {
  addToHistory,
  navigateDiagram,
  navigateToNode,
  openDiagramAndNavigateToNode,
  runSelectedNode,
  selectNode,
  setCenterNodeFromExternalPage,
  setDebugInfo,
  setDebugInfoBatch,
  setElements,
  setModuleId,
  forceSelectionOfNodes,
  setNodeResult,
  setSelectedCodeNode,
  setUpdateNodeResult,
  updateInterfaceError,
  updateLibrariesInstallLog,
  updateNodeConsole,
  updateNodeError,
  updateNodeInterfaceError
} from 'store/model/actions'
import { enqueueBlocker } from 'store/notifier/actions'
import { BlockerActions, NotificationLevels } from 'store/notifier/types'
import { setLastPath, setShowPreserveInstanceDialog } from 'store/user'
import { resetCustomDimensionsOnAIWidgetClose } from 'common/helpers/resetCustomDimensionsOnAIWidgetClose'
import { Process } from 'common/types/security'
import {
  getNoPermissionMessage,
  sectionPermissions
} from 'pages/interfaces/components/dynamicHtml/helpers'
import { LanguageValues } from 'common/types/instance'
import { AgentStatus, attachSavedMemory, updateSessionMessage } from 'store/agent'
import {
  documenterRunCompleted,
  documenterRunFailed,
  documenterRunProgress
} from 'store/documenter'
import { CodeNode, ModuleInfo } from 'common/types/model'
import { modelApi } from 'services/model'
import { CODE_NODE_PROPERTIES_TO_GET } from 'common/helpers/commonConstants'
import { getNotificationLink } from 'common/helpers/notifications'
import { readOnlyOrAppDefault } from 'common/helpers/readOnlyOrAppDefault'
import {
  removePendingAsyncOperation,
  setAsyncOperationResult,
  setAsyncOperationProgress,
  setAgentPresence
} from './actions'
import { AgentCaptureStatus, captureUserViewForAgent } from './agentCapture'
import {
  decideAgentActivity,
  displayedInterfaceId,
  FollowMemory,
  FollowTarget,
  forgetFollowedTarget,
  followTargetKey,
  followTargetLink,
  followTargetOf,
  isAlreadyShowing,
  presenceFrom
} from './agentLiveView'
import {
  selectFollowAgent,
  selectHasUnconfirmedUserChanges,
  selectIsEditingInterface
} from './selectors'

const NODE_COMPLETION_CHECK_INTERVAL = 100

function* processWSModelActions({
  messageType,
  message,
  title,
  progress,
  interfaceId,
  result,
  notLevel
}: SocketMessage) {
  switch (messageType) {
    case MessageType.OPENING_APP:
      // Only updates value/message. Hiding the loader is the job of whoever
      // is actually waiting for the open to finish (handleOpenApp's finally,
      // or the OPEN_APP_TASK_RESULT handler below) — reaching progress 100
      // here doesn't mean the app is usable yet in the async flow (see
      // plannings/open_app_async.md, Fase 4).
      yield put(setProgress({ value: progress || 0, message: title }))
      break
    case MessageType.NODE_BUTTON_FINISH_PROCESSING:
      // TODO: trigger end processing
      break
    case MessageType.APP_UNSAVED_CHANGES:
      yield put(setAppHasUnsavedChanges(Boolean(message)))
      break
    case MessageType.LIBRARIES_INSTALL_PROGRESS:
      yield put(updateLibrariesInstallLog(message, notLevel))
      break
    case MessageType.NODE_INTERFACE_RESULT:
      if (interfaceId && result) {
        yield put(setNodeResult(result))
        yield put(setApplicationComponent(interfaceId, result as any))
        yield put(setComponentIsLoading(interfaceId, [], false))
      }
      break
  }
}

function* callShowMsg({
  title,
  message,
  notLevel,
  autoHideDuration,
  extraParams
}: {
  title?: string
  message: string
  notLevel?: NotificationLevels
  autoHideDuration?: number
  extraParams?: string | null
}) {
  let link
  const { interface_id, section } = JSON.parse(extraParams || '{}')
  if (interface_id) {
    link = `/interfaces/${interface_id}`
  } else if (section) {
    link = `/${section}`
  }
  const notification = {
    key: getRandomKey(),
    title,
    message,
    link,
    options: {
      variant: notLevel || NotificationLevels.INFO,
      persist: true,
      autoHideDuration
    }
  }
  yield put(enqueue(notification))
}

function* processWSNotificationActions({ data, navigate }: SocketNotification) {
  const {
    messageType,
    title,
    message,
    notLevel,
    progress,
    closeWhenFinished,
    autoHideDuration,
    extra_params
  } = data
  switch (messageType) {
    case MessageType.STANDARD_MESSAGE:
      yield callShowMsg({ title, message, notLevel, autoHideDuration, extraParams: extra_params })
      break
    case MessageType.PROGRESS_BAR:
      if (progress === 100) {
        yield delay(200)
        yield put(
          setProgressBar({ show: true, value: 100, title, message, notLevel, closeWhenFinished })
        )
      } else {
        yield put(
          setProgressBar({
            show: true,
            value: progress || 0,
            title,
            message,
            notLevel,
            closeWhenFinished
          })
        )
      }
      break
    case MessageType.KILLED_INSTANCE:
      yield put(enqueueBlocker({ action: BlockerActions.LOGOUT, title, message }))
      yield sessionStorage.removeItem(INSTANCE_ID)
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
      break
  }
}

function* processWSInstanceResources(payload: SocketInstanceResources) {
  yield put(updateInstanceResources(payload))
}

function* processWSModelDebug(payload: socketModelDebug) {
  yield put(setDebugInfo(payload))
}

function* processWSModelDebugBatch(payload: socketModelDebugBatch) {
  if (payload.events?.length) {
    yield put(setDebugInfoBatch(payload.events))
  }
}

function* processWSNodeFeedback(payload: SocketNodeFeedback) {
  switch (payload.messageType) {
    case MessageType.SEND_NODE_ERROR:
      yield put(updateNodeError(payload.message))
      break
    case MessageType.SEND_NODE_CONSOLE:
      if (payload.message?.feedback || payload.message?.nodeId) {
        yield put(updateNodeConsole(payload.message))
      }
      break
  }
}

function* processWSInterfaceFeedback(payload: SocketInterfaceFeedback) {
  switch (payload.messageType) {
    case MessageType.SEND_INTERFACE_ERROR:
      yield put(updateInterfaceError(payload.message))
      break
  }
}

function* processWSNodeInterfaceFeedback(payload: SocketNodeInterfaceFeedback) {
  switch (payload.messageType) {
    case MessageType.SEND_NODE_INTERFACE_ERROR:
      yield put(updateNodeInterfaceError(payload.message))
      break
  }
}

const EDITING_WARNING_INTERVAL_MS = 60000
const EDITING_WARNING_TOAST_MS = 10000
let lastEditingWarning: { interfaceId?: string; at: number } = { at: 0 }

function* processWSInterfaceLayoutRefresh({
  interfaceId,
  interfaceCreated
}: SocketInterfaceLayoutRefreshMessage) {
  try {
    if (interfaceCreated) {
      yield put(notifyInterfacesListChanged())
    }
    if (!interfaceId) {
      yield put(setRefreshCurrentInterfaceLayout(true))
      return
    }
    const defaultInterfaceId: string | undefined = yield select(
      (state: ApplicationState) => state.app.info?.defaultInterfaceId
    )
    if (displayedInterfaceId(window.location.pathname, defaultInterfaceId) === interfaceId) {
      const editing: boolean = yield select(selectIsEditingInterface, interfaceId)
      if (editing) {
        const now = Date.now()
        if (
          interfaceId !== lastEditingWarning.interfaceId ||
          now - lastEditingWarning.at > EDITING_WARNING_INTERVAL_MS
        ) {
          lastEditingWarning = { interfaceId, at: now }
          yield put(
            enqueue({
              key: getRandomKey(),
              title: 'app.agentLive.interfaceChangedWhileEditing',
              message: 'app.agentLive.interfaceChangedWhileEditingDetail',
              options: {
                variant: NotificationLevels.WARNING,
                persist: false,
                autoHideDuration: EDITING_WARNING_TOAST_MS
              }
            })
          )
        }
        return
      }
      yield put(setRefreshCurrentInterfaceLayout(true))
    } else if (!interfaceCreated) {
      yield put(removeActiveTab(interfaceId))
      yield put(removeApplicationComponents(interfaceId))
    }
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* processWSInterfaceComponentResult(
  payload:
    | SocketInterfaceComponentsList
    | SocketInterfaceComponentResult
    | SocketInterfaceFilterComponentResult
    | SocketInterfaceFilterComponentsList
) {
  const interfaceId: string = payload.interfaceId
  switch (payload.messageType) {
    case MessageType.COMPONENTS_THAT_NEED_RENDERING:
      const componentsList = (payload as SocketInterfaceComponentsList).componentsList as string[]
      if (componentsList?.length > 0) {
        yield put(setComponentIsLoading(interfaceId, componentsList, true))
      }
      break
    case MessageType.FILTER_COMPONENTS_THAT_NEED_RENDERING:
      const filterComponentsList = (payload as SocketInterfaceFilterComponentsList).componentIds
      if (filterComponentsList?.length > 0) {
        yield put(setFilterComponentsLoading(filterComponentsList))
      }
      break
    case MessageType.COMPONENT_RESULT:
      const result = (payload as SocketInterfaceComponentResult).result
      const defaultInterfaceId: string | undefined = ((state: ApplicationState) =>
        state.app.info?.defaultInterfaceId)(yield select())
      const defaultInterfaceLayout: GridLayout | undefined = ((state: ApplicationState) =>
        state.app.interface.defaultInterfaceLayout)(yield select())
      const defaultInterfaceHasMenu = defaultInterfaceLayout?.components.some(
        (component) => 'menu' in component.properties
      )
      if (defaultInterfaceId === interfaceId && defaultInterfaceHasMenu) {
        yield put(setUpdateDefaultInterfaceLayout(result, interfaceId))
      }
      yield put(setApplicationComponent(interfaceId, result, payload.payloadChars))
      break
    case MessageType.REFRESH_CURRENT_INTERFACE:
      yield put(setRefreshCurrentInterface(true))
      break
    case MessageType.FILTER_RESULT:
      const resultFilter = (payload as SocketInterfaceFilterComponentResult).result
      yield put(addFilterToFilterComponentState(resultFilter))
      break
  }
}

function* processWSInstanceStatusActions({
  messageType,
  title,
  message,
  instance_id
}: SocketInstanceStatusMessage) {
  switch (messageType) {
    case MessageType.PURGED_SESSION:
      yield put(setShowPreserveInstanceDialog(false))
      yield put(enqueueBlocker({ action: BlockerActions.LOGOUT, title, message }))
      yield sessionStorage.removeItem(INSTANCE_ID)
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
      break
    case MessageType.PURGED_INSTANCE:
      yield put(setShowPreserveInstanceDialog(false))
      yield put(enqueueBlocker({ action: BlockerActions.CLOSE_INSTANCE, title, message }))
      yield sessionStorage.removeItem(INSTANCE_ID)
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
      break
    case MessageType.ENGINE_DOWN:
      yield put(setShowPreserveInstanceDialog(false))
      yield put(
        enqueueBlocker({
          action: BlockerActions.CLOSE_INSTANCE,
          title,
          message,
          instanceId: instance_id
        })
      )
      yield sessionStorage.removeItem(INSTANCE_ID)
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
      break
    case MessageType.INSTANCE_ABOUT_TO_EXPIRE:
      yield put(setShowPreserveInstanceDialog(true))
      break
  }
}

function* processWSAgentResponse({
  instanceId,
  message,
  messageType,
  chatSessionId,
  agentCode,
  error,
  status,
  metadata
}: SocketAgentMessage) {
  const documenterProgress = (metadata as any)?.documenter_progress
  const isDocumenterStatus =
    status === AgentStatus.DOCUMENTING_PROGRESS ||
    (status === AgentStatus.COMPLETED && !!documenterProgress)
  if (isDocumenterStatus && documenterProgress) {
    const runId = documenterProgress.run_id
    if (runId) {
      if (status === AgentStatus.COMPLETED) {
        if (error) {
          yield put(documenterRunFailed({ runId, error, progress: documenterProgress }))
        } else {
          yield put(documenterRunCompleted({ runId, progress: documenterProgress }))
        }
      } else {
        yield put(documenterRunProgress({ runId, progress: documenterProgress }))
      }
    }
    return
  }

  let chatSessions = ((state: ApplicationState) => state.agent.sessions)(yield select())
  if (!chatSessions) {
    return
  }

  let chatSession = Object.values(chatSessions).find(
    (session) => session.chatSessionId === chatSessionId
  )
  if (!chatSession) {
    return
  }

  if (status === AgentStatus.MEMORY_SAVED) {
    const memory = (metadata as any)?.memory
    if (memory?.id) {
      yield put(attachSavedMemory(chatSession.chatSessionCode, memory))
    }
    return
  }

  if (status === AgentStatus.TRACE_CREATED) {
    yield put(
      updateSessionMessage(
        chatSession.chatSessionCode,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        message
      )
    )
    return
  }

  const isReasoning = status === AgentStatus.REASONING
  const isUsingTool = status === AgentStatus.USING_TOOL
  const isExecutingTool = status === AgentStatus.EXECUTING_TOOL
  const isExecutingContextNodes = status === AgentStatus.EXECUTING_CONTEXT_NODES
  const isNamedStatus = isUsingTool || isExecutingTool || isExecutingContextNodes
  yield put(
    updateSessionMessage(
      chatSession.chatSessionCode,
      isReasoning || isNamedStatus ? undefined : message,
      status,
      metadata,
      isReasoning && message ? message : undefined,
      isNamedStatus && message ? message : undefined
    )
  )
}

function* processWSTraceUpdate({ traceId, traceStatus, span }: SocketTraceUpdateMessage) {
  yield call(() =>
    window.dispatchEvent(
      new CustomEvent('agent-trace-update', {
        detail: { traceId, traceStatus, span }
      })
    )
  )
}

function* processWSRefreshDiagram({ nodeId, nodes, moduleIds }: RefreshSocketMessage) {
  const state: ApplicationState = yield select()
  const moduleId = state.app.model.diagram?.moduleId
  const selectedNode = state.app.model.selectedNode
  const selectedNodeId = selectedNode?.identifier
  const isUnconfirmed = (id: string) =>
    state.app.model.codeDraftNodeId === id ||
    !!state.app.model.diagram?.unconfirmedNodes?.some((node) => node.identifier === id)
  const selectedNodeChanged =
    !!selectedNodeId &&
    !isUnconfirmed(selectedNodeId) &&
    Array.isArray(nodes) &&
    nodes.includes(selectedNodeId)

  if (window.location.pathname !== '/code') {
    if (selectedNodeChanged) {
      yield put(selectNode(selectedNodeId, true))
    }
    return
  }

  if (selectedNodeChanged) {
    yield put(selectNode(selectedNodeId))
  }

  const moduleOnScreenChanged = !moduleIds?.length || (!!moduleId && moduleIds.includes(moduleId))
  if (moduleId && moduleOnScreenChanged) {
    try {
      const moduleInfo: ModuleInfo = yield call(modelApi.navigateDiagram, moduleId)
      yield put(setElements(moduleInfo))
    } catch (error: any) {
      yield put(enqueue(error))
    }
  }
  if (nodeId && selectedNode?.identifier === nodeId && !isUnconfirmed(nodeId)) {
    const codeNode: CodeNode = yield call(
      modelApi.getCodeNodeProperties,
      nodeId,
      CODE_NODE_PROPERTIES_TO_GET
    )
    yield put(setSelectedCodeNode({ ...codeNode, isCalc: false }))
    if (codeNode?.definition) {
      yield put(addToHistory(codeNode.identifier, codeNode.definition))
    }
    yield put(setUpdateNodeResult())
  }
}

function* processDownloadFile({ filePath }: SocketDownloadMessage) {
  if (filePath) {
    try {
      const filenameArr = filePath.split('/')
      const fileName = filenameArr[filenameArr.length - 1]

      const progressFn = (progressEvent: ProgressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        store.dispatch(
          setProgressBar({
            show: true,
            value: progress || 0,
            title: undefined,
            message: fileName
          })
        )
      }

      const params = { sources: [filePath] }
      const response: AxiosResponse = yield call(
        fileManagerApi.downloadFileOrFolder,
        params,
        progressFn
      )
      yield call(saveAs, response.data, fileName)
      yield put(setProgressBar({ show: false, value: 0 }))
    } catch (error: any) {
      yield put(enqueue(error))
      yield put(setProgressBar({ show: false, value: 0 }))
    }
  }
}

function* processUploadFiles({ uploadParams }: SocketUploadMessage) {
  if (uploadParams) {
    try {
      uploadParams.show = true
      yield put(setUploaderParams(uploadParams))
    } catch (error: any) {
      yield put(enqueue(error))
    }
  }
}

function* processInternalTests({ statusTests }: SocketMessageInternalTests) {
  if (statusTests) {
    yield put(internalTestsStatus(statusTests))
  }
}

function* processAppLockInfo({ appIsLocked }: SocketAppInfo) {
  yield put(setAppLockInfo(appIsLocked))
  const filtersApplied = ((state: ApplicationState) => state.app.interface.filtersApplied)(
    yield select()
  )
  if (filtersApplied && !appIsLocked) {
    yield put(setFiltersApplied(false))
  }
}

function* processRefreshAppNotifications() {
  yield put(setRefreshAppNotifications(true))
  // The same push carries every process event that involves this person — a
  // process starting, a task landing on them, a status moving — so it is also
  // when "am I in a process at all" can have changed.
  yield put(checkHasActiveProcess())
}

function* processScheduledTaskUpdated(data: SocketScheduledTaskUpdated) {
  yield put(
    setScheduledTaskUpdate({
      taskId: data.taskId,
      state: data.state ?? null,
      enabled: data.enabled,
      isOpenApp: data.isOpenApp
    })
  )
}

function* processScheduledTaskFinished(data: SocketScheduledTaskFinished) {
  // Same inputs and precedence as the IntlProvider in main.tsx, so the toast is
  // in the language the rest of the UI is already showing.
  const instanceLanguage: string | undefined = yield select(
    (state: ApplicationState) => state.user.instanceData?.language
  )
  const locale = resolveAppLocale(instanceLanguage, localStorage.getItem(LOCALE))
  const { title, message } = getTaskFinishedNotification(locale, data.taskName, data.state)
  yield put(
    enqueue({
      key: getRandomKey(),
      title,
      message,
      options: {
        variant:
          data.state === ScheduleTaskStates.SUCCESS
            ? NotificationLevels.SUCCESS
            : NotificationLevels.ERROR,
        // Stays until dismissed: the run it reports on may have taken minutes
        // and the person who launched it was not necessarily watching.
        persist: true
      }
    })
  )
}

function* processWSAnalysisResponse({ analysisResponse }: SocketAnalysisResponse) {
  yield put(setAnalysisResult(analysisResponse))
}

function* processWSTranslationsProgress({ progress }: SocketMessageTranslationsProgress) {
  yield put(setTranslationsProgress(progress))
}

function* processWSNavigateToSection(
  { section, params }: SocketNavigateToSectionMessage,
  navigate?: NavigateFunction
) {
  if (!!navigate) {
    const processes: Process[] = yield select((state) => state.user.instanceData.process)
    const normalizedSection = section.startsWith('/') ? section : `/${section}`
    const requiredPermissions = sectionPermissions[normalizedSection]
    if (requiredPermissions?.length) {
      const hasAllPermissions = requiredPermissions.every((perm) =>
        processes.includes(Process[perm])
      )
      const language: LanguageValues = yield select((state) => {
        const appLang = state.app.info?.applicationLanguage
        return appLang && appLang !== '' ? appLang : state.user.instanceData.language
      })
      if (!hasAllPermissions) {
        const message = getNoPermissionMessage(language, normalizedSection)
        const notification = {
          key: getRandomKey(),
          message,
          options: {
            variant: NotificationLevels.WARNING
          }
        }
        yield put(enqueue(notification))
        return
      }
    }
    switch (section) {
      case NavigationSection.CODE:
        const nodeId: string = params?.node_id
        const moduleId: string = params?.module_id
        const codeLayoutId: number = params?.code_layout_id
        const runNode: boolean = params?.run_node
        yield put(openDiagramAndNavigateToNode(nodeId, navigate))
        yield put(navigateToNode(moduleId, nodeId))
        yield put(setCenterNodeFromExternalPage(nodeId))
        if (codeLayoutId) {
          resetCustomDimensionsOnAIWidgetClose(store.dispatch)
        }
        if (runNode) {
          yield put(selectNode(nodeId, false))
          yield put(runSelectedNode())
        }
        break
      case NavigationSection.INTERFACES:
        const interfaceId: string = params?.interface_id
        if (interfaceId) {
          navigate(`/interfaces/${interfaceId}`)
        } else {
          navigate('/interfaces')
        }
        break
      case NavigationSection.HOME:
        navigate('/')
        break
      case NavigationSection.FILES:
        const folderPath: string = params?.folder_path
        if (folderPath) {
          yield put(setLastPath({ path: folderPath, isMyWorkspace: false }))
          navigate(`/${section}`)
        } else {
          navigate('/files')
        }
        break
      default:
        navigate(`/${section}`)
        break
    }
  }
}

function* processWSOpenApp({ params }: SocketOpenAppMessage, navigate?: NavigateFunction) {
  const appName = params.folder.split('/').pop()
  const hasUnsavedChanges = ((state: ApplicationState) => state.app.hasUnsavedChanges)(
    yield select()
  )
  if (hasUnsavedChanges) {
    yield put(setOpenAppWithUnconfirmedChanges(params as any))
  } else {
    yield put(setOpeningAppName(appName))
    if (params.showVersionsOnOpen) {
      yield put(
        setAppVersionsDialog(
          true,
          params.folder,
          params.defaultVersion,
          readOnlyOrAppDefault(params.readOnly),
          params.openOnNewInstance,
          params.appProperties,
          params.canWriteInDir,
          params.resources || undefined,
          params.interfaceIdToOpenOnStart || undefined
        )
      )
    } else if (!!navigate) {
      const openAppParams = {
        fullPath: params.folder,
        navigate,
        openAsReadOnly: readOnlyOrAppDefault(params.readOnly),
        version: params.version || undefined,
        resources: params.resources || undefined,
        interface: params.interfaceIdToOpenOnStart || undefined
      }
      // The while loop is necessary so that when opening an app using pp.open_app, the node finishes running before opening the app; otherwise, it will return a 500 error.
      let attempts = 0
      const maxAttempts = 10
      while (attempts < maxAttempts) {
        const runningNode: string | undefined = yield select(
          (state: ApplicationState) => state.app.model.runningNode
        )
        if (!runningNode) break
        yield delay(NODE_COMPLETION_CHECK_INTERVAL)
        attempts++
      }
      if (params.openOnNewInstance) {
        yield put(openAppOnNewInstance(openAppParams))
      } else {
        yield put(openApp(openAppParams))
      }
    }
  }
}

function* processAppNotificationMessage({
  messageType,
  notificationType,
  message,
  extraParams
}: SocketAppNotificationMessage) {
  const link = getNotificationLink(notificationType, extraParams)

  const notification = {
    key: getRandomKey(),
    message,
    link,
    options: {
      variant: NotificationLevels.INFO,
      persist: true
    }
  }
  yield put(enqueue(notification))
}

function* processFileOperationProgress({
  operationId,
  operationType,
  percent,
  current,
  total,
  fileName
}: SocketFileOperationProgressMessage) {
  // Set persistent operation on first progress update
  const persistentOperation = ((state: ApplicationState) => state.socket?.persistentOperation)(
    yield select()
  )

  if (!persistentOperation) {
    yield put(
      setPersistentOperation({
        operationId,
        operationType,
        startedAt: Date.now()
      })
    )
    // Start polling for status updates
    yield put(startOperationStatusPolling(operationId, operationType))
  }

  yield put(
    setAsyncOperationProgress({
      operationId,
      operationType,
      percent,
      current,
      total,
      fileName
    })
  )
}

function* processFileOperationComplete({
  operationId,
  operationType,
  success,
  result,
  error
}: SocketAsyncOperationCompleteMessage) {
  yield put(removePendingAsyncOperation(operationId))

  yield put(
    setAsyncOperationResult({
      operationId,
      operationType,
      success,
      result,
      error
    })
  )

  // Clear persistent operation when operation completes
  yield put(clearPersistentOperation())
  // Stop polling
  yield put(stopOperationStatusPolling())
}

const FOLLOW_AGENT_DEBOUNCE_MS = 400
const AGENT_ACTIVITY_TOAST_MS = 6000
const HIGHLIGHT_WAIT_MS = 1500
let followAgentSeq = 0
let followMemory: FollowMemory = {}

const AGENT_ACTIVITY_TITLES: Record<string, string> = {
  'node:created': 'app.agentLive.nodeCreated',
  'node:updated': 'app.agentLive.nodeUpdated',
  'module:updated': 'app.agentLive.nodesMoved',
  'module:deleted': 'app.agentLive.nodesDeleted',
  'interface:created': 'app.agentLive.interfaceCreated',
  'interface:updated': 'app.agentLive.interfaceUpdated'
}

function* processWSAgentPresence({ attached, expiresIn, clientName }: SocketAgentPresenceMessage) {
  try {
    yield put(setAgentPresence(presenceFrom(attached, expiresIn, clientName, Date.now())))
    if (!attached) {
      followMemory = {}
    }
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* followAgentTo(target: FollowTarget, navigate: NavigateFunction) {
  switch (target.kind) {
    case 'node':
      yield call(
        processWSNavigateToSection,
        {
          messageType: MessageType.NAVIGATE_TO_SECTION,
          section: NavigationSection.CODE,
          params: { node_id: target.nodeId, module_id: target.moduleId }
        },
        navigate
      )
      break
    case 'module':
      if (window.location.pathname === '/code') {
        yield put(navigateDiagram(target.moduleId))
      } else {
        yield put(setModuleId(target.moduleId))
        navigate('/code')
      }
      break
    case 'interface':
      yield call(
        processWSNavigateToSection,
        {
          messageType: MessageType.NAVIGATE_TO_SECTION,
          section: NavigationSection.INTERFACES,
          params: { interface_id: target.interfaceId }
        },
        navigate
      )
      break
  }
}

function* highlightChangedNodes(nodeIds: string[], moduleId?: string | null) {
  if (window.location.pathname !== '/code') {
    return
  }
  yield race({
    refreshed: take(ModelActionTypes.SET_ELEMENTS),
    timeout: delay(HIGHLIGHT_WAIT_MS)
  })
  const moduleOnScreen: string | undefined = yield select(
    (state: ApplicationState) => state.app.model.diagram?.moduleId
  )
  if (window.location.pathname === '/code' && (!moduleId || moduleId === moduleOnScreen)) {
    yield put(forceSelectionOfNodes(nodeIds))
  }
}

function* processWSAgentActivity(
  activity: SocketAgentActivityMessage,
  navigate?: NavigateFunction
) {
  try {
    const target = followTargetOf(activity)
    if (!target) {
      return
    }
    const beforeChange = activity.phase === 'before'
    const seq = ++followAgentSeq
    if (!beforeChange) {
      yield delay(FOLLOW_AGENT_DEBOUNCE_MS)
      if (seq !== followAgentSeq) {
        return
      }
    }

    const state: ApplicationState = yield select()
    const screen = {
      pathname: window.location.pathname,
      moduleId: state.app.model.diagram?.moduleId,
      selectedNodeId: state.app.model.selectedNode?.identifier,
      defaultInterfaceId: state.app.info?.defaultInterfaceId
    }
    const canFollow =
      !!navigate && selectFollowAgent(state) && !selectHasUnconfirmedUserChanges(state)
    const { decision, memory } = decideAgentActivity(
      followTargetKey(target),
      { alreadyShowing: isAlreadyShowing(target, screen), canFollow, beforeChange },
      followMemory
    )
    followMemory = memory
    if (decision === 'follow' && navigate) {
      yield call(followAgentTo, target, navigate)
    }
    if (!beforeChange && canFollow && activity.highlightNodeIds?.length) {
      yield call(highlightChangedNodes, activity.highlightNodeIds, activity.moduleId)
    }
    if (decision !== 'toast') {
      return
    }
    const title = AGENT_ACTIVITY_TITLES[`${target.kind}:${activity.action}`]
    if (!title) {
      return
    }
    yield put(
      enqueue({
        key: getRandomKey(),
        title,
        message:
          activity.title ??
          (target.kind === 'node'
            ? target.nodeId
            : target.kind === 'module'
            ? target.moduleId
            : target.interfaceId),
        link: followTargetLink(target),
        options: {
          variant: NotificationLevels.INFO,
          persist: false,
          autoHideDuration: AGENT_ACTIVITY_TOAST_MS
        }
      })
    )
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* processWSAppInfoChanged(
  { application, versionChanged, versionKeepsSession }: SocketAppInfoChangedMessage,
  navigate?: NavigateFunction
) {
  try {
    if (!application) {
      return
    }
    if (versionChanged && navigate) {
      if (versionKeepsSession) {
        yield call(confirmNodeDefinitionsBeforeSaving, true)
      }
      if (window.location.pathname.startsWith('/interfaces/')) {
        navigate('/')
        yield delay(0)
      }
      yield call(applySwitchedAppVersion, application, navigate)
      return
    }
    yield put(setAppInfo(application))
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* processWSAppClosedByAgent(navigate?: NavigateFunction) {
  try {
    yield put(setAgentPresence(undefined))
    followMemory = {}
    if (navigate) {
      yield put(closeAppFromOtherTab(navigate, 'app_closed_by_agent'))
    }
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

// The notice sits over the content, so a burst of captures shows it only once and
// later pictures are not covered by it.
const CAPTURE_NOTICE_INTERVAL_MS = 5 * 60 * 1000
let lastCaptureNoticeAt = 0

function* processWSCaptureUserView(data: SocketCaptureUserViewMessage) {
  try {
    const defaultInterfaceId: string | undefined = yield select(
      (state: ApplicationState) => state.app.info?.defaultInterfaceId
    )
    const status: AgentCaptureStatus = yield call(
      captureUserViewForAgent,
      data.requestId,
      data.settleMs ?? 0,
      displayedInterfaceId(window.location.pathname, defaultInterfaceId)
    )
    if (status === 'ready' && Date.now() - lastCaptureNoticeAt > CAPTURE_NOTICE_INTERVAL_MS) {
      lastCaptureNoticeAt = Date.now()
      yield put(
        enqueue({
          key: getRandomKey(),
          message: 'app.agentLive.viewCaptured',
          options: { variant: NotificationLevels.INFO, autoHideDuration: 3000 }
        })
      )
    }
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* processOpenAppTaskResult(payload: SocketOpenAppTaskResultMessage) {
  yield put(setOpenAppTaskResult(payload))
}

function* initializeWebSocketServer(navigate?: NavigateFunction) {
  const instanceData = ((state: ApplicationState) => state.user.instanceData)(yield select())
  if (instanceData) {
    const { id: instanceId } = instanceData
    const channel: Channel<ActionPattern> = yield call(createEventServer, instanceId)
    try {
      while (true) {
        const { data, type }: { data: any; type?: SocketActionTypes } = yield take(channel)
        if (type) {
          yield put({ type })
        } else {
          switch (data.messageType) {
            // ws is asking to update model or node properties
            case MessageType.OPENING_APP:
            case MessageType.NODE_BUTTON_FINISH_PROCESSING:
            case MessageType.APP_UNSAVED_CHANGES:
            case MessageType.LIBRARIES_INSTALL_PROGRESS:
            case MessageType.NODE_INTERFACE_RESULT:
              yield fork(processWSModelActions, data)
              break

            // ws is asking to show debug info
            case MessageType.NODE_DEBUG_INFORMATION:
              yield fork(processWSModelDebug, data)
              break

            case MessageType.NODE_DEBUG_INFORMATION_BATCH:
              yield fork(processWSModelDebugBatch, data)
              break

            // ws is asking to send a notification
            case MessageType.STANDARD_MESSAGE:
            case MessageType.PROGRESS_BAR:
            case MessageType.KILLED_INSTANCE:
              const params: SocketNotification = { data, navigate }
              yield fork(processWSNotificationActions, params)
              break

            // ws is asking to send instance resources info
            case MessageType.UPDATE_INSTANCE_RESOURCES:
              yield fork(processWSInstanceResources, data)
              break
            // ws is asking to send node feedback
            case MessageType.SEND_NODE_ERROR:
            case MessageType.SEND_NODE_CONSOLE:
              yield fork(processWSNodeFeedback, data)
              break
            case MessageType.SEND_INTERFACE_ERROR:
              yield fork(processWSInterfaceFeedback, data)
              break
            case MessageType.SEND_NODE_INTERFACE_ERROR:
              yield fork(processWSNodeInterfaceFeedback, data)
              break
            case MessageType.COMPONENTS_THAT_NEED_RENDERING:
            case MessageType.COMPONENT_RESULT:
            case MessageType.REFRESH_CURRENT_INTERFACE:
            case MessageType.FILTER_RESULT:
              yield fork(processWSInterfaceComponentResult, data)
              break
            case MessageType.REFRESH_CURRENT_INTERFACE_LAYOUT:
              yield fork(processWSInterfaceLayoutRefresh, data)
              break
            case MessageType.PURGED_SESSION:
            case MessageType.PURGED_INSTANCE:
            case MessageType.ENGINE_DOWN:
            case MessageType.INSTANCE_ABOUT_TO_EXPIRE:
              yield fork(processWSInstanceStatusActions, data)
              break
            case MessageType.AGENT_RESPONSE:
              yield fork(processWSAgentResponse, data)
              break
            case MessageType.AGENT_TRACE_UPDATE:
              yield fork(processWSTraceUpdate, data)
              break
            case MessageType.DOWNLOAD_FILE:
              yield fork(processDownloadFile, data)
              break
            case MessageType.UPLOAD_FILES:
              yield fork(processUploadFiles, data)
              break
            // ws internal tests
            case MessageType.INTERNAL_TESTS_STATUS:
              yield fork(processInternalTests, data)
              break
            case MessageType.APP_LOCK_INFO:
              yield fork(processAppLockInfo, data)
              break
            case MessageType.REFRESH_APP_NOTIFICATIONS:
              yield fork(processRefreshAppNotifications)
              break
            case MessageType.SCHEDULED_TASK_UPDATED:
              yield fork(processScheduledTaskUpdated, data)
              break
            case MessageType.SCHEDULED_TASK_FINISHED:
              yield fork(processScheduledTaskFinished, data)
              break
            case MessageType.ANALYSIS_RESPONSE:
              yield fork(processWSAnalysisResponse, data)
              break
            case MessageType.TRANSLATIONS_PROGRESS:
              yield fork(processWSTranslationsProgress, data)
              break
            case MessageType.NAVIGATE_TO_SECTION:
              yield fork(processWSNavigateToSection, data, navigate)
              break
            case MessageType.OPEN_APP:
              yield fork(processWSOpenApp, data, navigate)
              break
            case MessageType.REFRESH_DIAGRAM:
              yield fork(processWSRefreshDiagram, data)
              break
            case MessageType.APP_NOTIFICATION_MESSAGE:
              yield fork(processAppNotificationMessage, data)
              break
            case MessageType.FILE_OPERATION_PROGRESS:
              yield fork(processFileOperationProgress, data)
              break
            case MessageType.FILE_OPERATION_COMPLETE:
              yield fork(processFileOperationComplete, data)
              break
            case MessageType.OPEN_APP_TASK_RESULT:
              yield fork(processOpenAppTaskResult, data)
              break
            // Scenario selection changed outside this tab (the AI agent). Same
            // signal the "Select scenarios" dialog dispatches.
            case MessageType.SCENARIOS_SELECTION_CHANGED:
              yield put(notifyScenariosChanged())
              break
            case MessageType.HTML_INTERFACE_BROADCAST: {
              const {
                event,
                data: messageData,
                targetInterfaceId,
                from
              } = data as SocketHtmlInterfaceBroadcast
              yield put(
                receiveHtmlInterfaceBroadcast({
                  event,
                  data: messageData,
                  targetInterfaceId,
                  from
                })
              )
              break
            }
            case MessageType.AGENT_PRESENCE:
              yield fork(processWSAgentPresence, data)
              break
            case MessageType.AGENT_ACTIVITY:
              yield fork(processWSAgentActivity, data, navigate)
              break
            case MessageType.APP_INFO_CHANGED:
              yield fork(processWSAppInfoChanged, data, navigate)
              break
            case MessageType.APP_CLOSED_BY_AGENT:
              yield fork(processWSAppClosedByAgent, navigate)
              break
            case MessageType.CAPTURE_USER_VIEW:
              yield fork(processWSCaptureUserView, data)
              break
          }
        }
      }
    } finally {
      // takeLatest cancels this task whenever a newer START_SERVER arrives, and without
      // closing the channel the eventChannel's cleanup never runs — leaving the previous
      // socket open forever instead of torn down. Safe to close here only because
      // that cleanup closes the socket IT created, not whichever one is active.
      channel.close()
    }
  }
}

export function* startStopServer({ payload: { navigate } }: AnyAction) {
  yield race({
    task: call(initializeWebSocketServer, navigate),
    cancel: take(SocketActionTypes.STOP_SERVER)
  })
  ws?.close()
}

export function* stopServer() {
  yield ws?.close()
}

/**
 * Polls the async operation status endpoint every 30 seconds.
 * This acts as a defensive mechanism in case WebSocket connection is lost.
 * Stops polling when operation completes or fails, or when explicitly stopped.
 *
 * Safety mechanisms:
 * - Inactivity timeout: fails only when no progress is detected for a long period.
 * - Consecutive polling errors limit: avoids failing on transient network issues.
 */
function* pollOperationStatus(operationId: string, operationType: string) {
  const POLL_INTERVAL_MS = 30000 // 30 seconds
  const MAX_INACTIVITY_MS = 30 * 60 * 1000 // 30 minutes without progress
  const MAX_CONSECUTIVE_POLL_ERRORS = 6 // ~3 minutes with 30s interval

  let lastActivityAt = Date.now()
  let lastProgressSignature: string | null = null
  let consecutivePollErrors = 0
  let shouldContinuePolling = true

  while (shouldContinuePolling) {
    yield delay(POLL_INTERVAL_MS)

    try {
      const now = Date.now()
      if (now - lastActivityAt > MAX_INACTIVITY_MS) {
        console.error(
          `Polling inactivity timeout: operation ${operationId} had no progress for ${
            MAX_INACTIVITY_MS / 60000
          } minutes`
        )
        yield put(removePendingAsyncOperation(operationId))
        yield put(
          setAsyncOperationResult({
            operationId,
            operationType: operationType as any,
            success: false,
            result: undefined,
            error: 'operation_inactivity_timeout'
          })
        )
        yield put(clearPersistentOperation())
        shouldContinuePolling = false
        continue
      }

      const status: Awaited<ReturnType<typeof fileManagerApi.getAsyncOperationStatus>> = yield call(
        fileManagerApi.getAsyncOperationStatus.bind(fileManagerApi),
        operationId
      )

      consecutivePollErrors = 0

      if (!status || !status.status) {
        console.warn(`Invalid status response for operation ${operationId}:`, status)
        consecutivePollErrors += 1
        continue
      }

      // Handle different status responses
      if (status.status === 'in_progress') {
        const progressSignature = `${status.percent}|${status.current_file_index}`
        if (progressSignature !== lastProgressSignature) {
          lastProgressSignature = progressSignature
          lastActivityAt = Date.now()
        }

        const currentProgress = ((state: ApplicationState) => state.socket?.operationProgress)(
          yield select()
        )

        yield put(
          setAsyncOperationProgress({
            operationId,
            operationType: status.operation_type,
            percent: status.percent,
            current: status.current_file_index,
            total: status.total_files,
            fileName: currentProgress?.fileName || ''
          })
        )
      } else if (status.status === 'completed') {
        yield put(removePendingAsyncOperation(operationId))
        yield put(
          setAsyncOperationResult({
            operationId,
            operationType: status.operation_type,
            success: true,
            result: undefined,
            error: undefined
          })
        )
        yield put(clearPersistentOperation())
        shouldContinuePolling = false
      } else if (status.status === 'failed') {
        yield put(removePendingAsyncOperation(operationId))
        yield put(
          setAsyncOperationResult({
            operationId,
            operationType: status.operation_type,
            success: false,
            result: undefined,
            error: status.error
          })
        )
        yield put(clearPersistentOperation())
        shouldContinuePolling = false
      } else {
        // Unknown status: retry up to MAX_CONSECUTIVE_POLL_ERRORS to tolerate transient backend issues
        console.warn(`Unknown operation status for ${operationId}:`, status)
        consecutivePollErrors += 1
      }
    } catch (error) {
      console.error(`Error polling operation status:`, error)
      consecutivePollErrors += 1
    }

    if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
      console.error(
        `Polling status unreachable for operation ${operationId}: exceeded max consecutive polling errors`
      )
      yield put(removePendingAsyncOperation(operationId))
      yield put(
        setAsyncOperationResult({
          operationId,
          operationType: operationType as any,
          success: false,
          result: undefined,
          error: 'operation_status_unreachable'
        })
      )
      yield put(clearPersistentOperation())
      shouldContinuePolling = false
    }
  }
}

/**
 * Saga that starts polling when START_OPERATION_STATUS_POLLING is dispatched
 * and stops when STOP_OPERATION_STATUS_POLLING is dispatched.
 */
function* watchOperationStatusPolling() {
  yield takeLatest(
    SocketActionTypes.START_OPERATION_STATUS_POLLING,
    function* ({ payload }: AnyAction) {
      const { operationId, operationType } = payload

      yield race({
        poll: call(pollOperationStatus, operationId, operationType),
        stop: take(SocketActionTypes.STOP_OPERATION_STATUS_POLLING)
      })
    }
  )
}

function forgetFollowedTargetWhenFollowTurnsOn({ payload: follow }: AnyAction) {
  if (follow) {
    followMemory = forgetFollowedTarget(followMemory)
  }
}

function* socketSaga() {
  yield all([
    takeLatest(SocketActionTypes.START_SERVER, startStopServer),
    takeLatest(SocketActionTypes.SET_FOLLOW_AGENT, forgetFollowedTargetWhenFollowTurnsOn),
    takeLatest(SocketActionTypes.STOP_SERVER, stopServer),
    fork(watchOperationStatusPolling)
  ])
}

export { socketSaga, ws }
